import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n, useTranslate } from '../../contexts/I18nContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { useSiteLogo } from '../../contexts/SiteLogoContext';
import { Loader2, Mail, Lock, User, Building2, ArrowLeft } from 'lucide-react';

export const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { t } = useI18n();
  const { translateText } = useTranslate();
  const { getFullLogoUrl, siteName } = useSiteLogo();

  const isProviderType = searchParams.get('type') === 'provider';
  const referralCode = searchParams.get('ref') || '';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    isProvider: isProviderType || !!referralCode,
    businessName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(translateText('Las contraseñas no coinciden'));
      return;
    }

    if (formData.password.length < 6) {
      setError(translateText('La contraseña debe tener al menos 6 caracteres'));
      return;
    }

    if (formData.isProvider && !formData.businessName) {
      setError(translateText('El nombre del negocio es requerido para proveedores'));
      return;
    }

    setLoading(true);

    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.isProvider ? 'provider' : 'user',
        business_name: formData.isProvider ? formData.businessName : undefined,
        referral_code: referralCode || undefined
      });

      if (user.role === 'provider') {
        navigate('/provider');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] flex items-center justify-center px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          className="mb-6 rounded-full"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>

        <Card className="rounded-3xl shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center mx-auto mb-4">
              {getFullLogoUrl() ? (
                <img src={getFullLogoUrl()} alt={siteName} className="h-14 object-contain" />
              ) : (
                <div className="w-24 h-24 bg-[#00CC66] rounded-2xl flex items-center justify-center">
                  <span className="text-white font-bold text-3xl">{siteName.charAt(0)}</span>
                </div>
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-[#064E3B]">
              {t('registerTitle')}
            </CardTitle>
            <CardDescription>
              {translateText('Crea tu cuenta para comenzar')}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="rounded-xl">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">{t('name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={translateText('Tu nombre')}
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="register-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{translateText('Confirmar contraseña')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2 py-2">
                <Checkbox
                  id="isProvider"
                  name="isProvider"
                  checked={formData.isProvider}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isProvider: checked }))}
                  data-testid="register-provider-check"
                />
                <Label htmlFor="isProvider" className="text-sm cursor-pointer">
                  {t('loginAsProvider')}
                </Label>
              </div>

              {formData.isProvider && (
                <div className="space-y-2 animate-fadeIn">
                  <Label htmlFor="businessName">{t('businessName')}</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="businessName"
                      name="businessName"
                      type="text"
                      placeholder={translateText('Nombre de tu negocio')}
                      value={formData.businessName}
                      onChange={handleChange}
                      className="pl-10 rounded-xl"
                      data-testid="register-business-name"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {translateText('Creando cuenta...')}
                  </>
                ) : (
                  t('registerTitle')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                {t('hasAccount')}{' '}
                <Link to="/login" className="text-[#00CC66] font-medium hover:underline">
                  {t('loginTitle')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
